import { CacheControl } from '@tusbar/cache-control'

import { ApolloClientProfile, Client } from '@syconium/weeping-figs'

import { FetchBulkLayoutQuery } from '../../../__generated__/graphql/catalog/graphql'
import { NotFoundPage } from '../../../app/_not-found.client'
import {
  CONTENT_KEY,
  INCLUDE_UNAVAILABLE_PRODUCTS,
  PREVIEW,
  PRODUCT_DATA_KEY,
} from '../../../brunswick/constants'
import { QuickBuyContainer } from '../../../brunswick/containers/QuickBuyContainer'
import { MagnoliaPage, MagnoliaPageInitialProps } from '../../../components/pages/MagnoliaApp'
import { FETCH_BULK_LAYOUT } from '../../../lib/graphql/queries'
import { cachePolicies } from '../../../lib/response-cache'
import { singleQueryParamValue } from '../../../lib/utils'

import { BulkView } from './BulkView'
import { BULK_COLLECTION_HANDLE, GENDER_QUERY_PARAM } from './constants'
import { BulkContainer } from './containers/BulkContainer'
import { isCollectionGridSection, isCollectionViewHeroSection } from './type-guards'

import type {
  CollectionView,
  CollectionViewHeroSection,
  GenderKey,
  VisualFilterTile,
} from './types'

type Props = MagnoliaPageInitialProps<{
  heroSection: CollectionViewHeroSection | null
  categoryFilters: Array<VisualFilterTile> | null
  errors: ReadonlyArray<Error>
  gender: GenderKey
  title: CollectionView['title'] | null
  description: CollectionView['socialDescription'] | null
  image: CollectionView['socialImage'] | null
  noindex: boolean
}>

type QueryOptions = {
  includeUnavailable: boolean
  isPreview: boolean
}

export const BulkPage: MagnoliaPage<Props> = ({
  heroSection,
  categoryFilters,
  errors,
  gender,
  title,
  description,
  image,
  noindex,
}) => {
  if (errors.length > 0 || !heroSection || !categoryFilters || !gender) {
    return <NotFoundPage />
  }

  return (
    <QuickBuyContainer.Provider>
      <BulkContainer.Provider initialState={{ gender, categoryFilters }}>
        <BulkView
          title={title}
          description={description}
          image={image}
          heroSection={heroSection}
          noindex={noindex}
        />
      </BulkContainer.Provider>
    </QuickBuyContainer.Provider>
  )
}

BulkPage.getInitialProps = async function (context) {
  const includeUnavailable = context.query[PRODUCT_DATA_KEY] === INCLUDE_UNAVAILABLE_PRODUCTS
  const isPreview = singleQueryParamValue(context.query[CONTENT_KEY], '') === PREVIEW
  const figsPublicClient = context.apolloClientFactory.for(ApolloClientProfile.FigsPublicSupergraph)
  const genderKey = singleQueryParamValue(context.query[GENDER_QUERY_PARAM], 'women')
  const gender: GenderKey = genderKey === 'men' ? 'men' : 'women'

  const queryOptions: QueryOptions = {
    includeUnavailable,
    isPreview,
  }

  const {
    data,
    error,
    errors: fetchErrors,
  } = await fetchBulkLayout(figsPublicClient, BULK_COLLECTION_HANDLE, queryOptions)

  const { collectionView } = data || {}
  if (!collectionView) {
    return {
      gender: 'women',
      heroSection: null,
      categoryFilters: null,
      errors: [...(error ? [error] : []), ...(fetchErrors ?? [])],
      responseCacheControl: cachePolicy(fetchErrors ?? [], includeUnavailable, isPreview),
      title: null,
      description: null,
      image: null,
      noindex: true,
    }
  }

  const collectionGridSection = collectionView.sections.find(isCollectionGridSection) || null

  const heroSection = collectionView.sections.find(isCollectionViewHeroSection) || null

  const categoryFilters = collectionGridSection?.visualFiltersSectionV2?.tiles ?? []

  const errors = [...(error ? [error] : []), ...(fetchErrors ?? [])]

  return {
    gender,
    heroSection,
    categoryFilters,
    errors,
    responseCacheControl: cachePolicy(errors, includeUnavailable, isPreview),
    title: collectionView.title ?? null,
    description: collectionView.socialDescription ?? null,
    image: collectionView.socialImage ?? null,
    noindex: collectionView.noindex ?? true,
  }
}

export function normalizeCategoryFilter(value: string) {
  return value ? value.replace(/s$/, '') : ''
}

function fetchBulkLayout(
  client: Client,
  handle: string,
  { includeUnavailable, isPreview }: QueryOptions
) {
  return client.query<FetchBulkLayoutQuery>({
    query: FETCH_BULK_LAYOUT,
    variables: { handle },
    context: { includeUnavailable, isPreview },
    errorPolicy: 'all',
  })
}

function cachePolicy(
  errors: ReadonlyArray<Error>,
  includeUnavailable: boolean,
  isPreview: boolean
): CacheControl {
  const shouldNotCache = includeUnavailable || isPreview || errors.length > 0
  const policy = shouldNotCache ? cachePolicies.noCache : cachePolicies.publicHtmlPolicy
  return policy
}
